// The escalation family's faces (#3769 phase 3): a session escalation, a babysit
// park, an escalated or already-covered request, the onboarding wait, a key outage.
// Same rules as `notification-copy.ts`: one zod parse per producer, `null` falls back
// to the server's text.
//
// WHERE THE SERVER'S TEXT IS KEPT, and why each time:
//   - a session escalation's BODY: it carries the model's summary and reason, which
//     never ride the payload, and model prose is never translated;
//   - the opened key outage's BODY: its per-step held counts are deliberately not on
//     the payload (`keyOutageNotification`, `@developerz/domain`).
// Everything else here is spelled whole. What the server body adds and the payload
// lacks (a park's machine detail, an escalated request's verdict reason, the
// onboarding pass's own sentence) is left to the deep link, never invented.

import { EscalationCategorySchema } from '@developerz/domain';
import type { Translator } from '@developerz/i18n';
import { z } from 'zod';
import { BABYSIT_COPY_KEYS, BABYSIT_RERUN_REFUSED_KEY } from './notification-babysit-copy';
import { count, face, type Renderer, stamp, text } from './notification-copy-face';
import { ESCALATION_TITLE_KEY } from './notification-escalation-copy';

/**
 * `packages/agent/src/escalation/dispatch.ts` `escalationNotification`. Strict, so a
 * payload carrying anything else (a babysit park's `babysit_reason`, `head_sha`) is
 * another producer's. The body is the server's: see the header.
 */
const escalation = face(
  z
    .object({
      escalation_id: text,
      category: EscalationCategorySchema,
      decision_reason: z.string(),
      repo: text.optional(),
      pr_number: count.optional(),
      issue_id: z.string().optional(),
      update_seq: count.optional(),
      face: stamp('escalation'),
    })
    .strict(),
  (a, tr, row) => {
    const title = tr(ESCALATION_TITLE_KEY[a.category], {
      on: a.repo ?? tr('notifications.kind.escalation.no_repo'),
    });
    const update = a.update_seq !== undefined && a.update_seq > 0;
    return {
      title: update ? tr('notifications.kind.escalation.updated', { title }) : title,
      body: row.body,
    };
  },
);

const BABYSIT_REASONS = Object.keys(BABYSIT_COPY_KEYS) as [
  keyof typeof BABYSIT_COPY_KEYS,
  ...(keyof typeof BABYSIT_COPY_KEYS)[],
];

/** `apps/worker/src/babysit/escalation.ts` `babysitEscalationNotification`. */
const babysit = face(
  z.object({
    face: z.literal('babysit'),
    repo: text,
    pr_number: count,
    babysit_reason: z.enum(BABYSIT_REASONS),
    rerun_refused: z.boolean().optional(),
  }),
  (a, tr) => {
    const keys = BABYSIT_COPY_KEYS[a.babysit_reason];
    const rerun = a.babysit_reason === 'ci_fix_declined' && a.rerun_refused === true;
    return {
      title: tr(keys.title, { pr: `${a.repo} #${String(a.pr_number)}` }),
      body: tr(rerun ? BABYSIT_RERUN_REFUSED_KEY : keys.sentence),
    };
  },
);

/**
 * `packages/agent/src/triage/escalate-notice.ts` `escalateAnswerNotification`. The
 * ask's text is withheld (it may be unscreened) and the verdict's reason is model
 * prose, so neither is spelled.
 */
const escalateAnswer = face(
  z
    .object({
      rawTaskId: text,
      repoFullNames: z.array(text),
      originRef: z.unknown(),
      degraded: z.boolean(),
    })
    .strict(),
  (a, tr) => ({
    title: tr('notifications.kind.escalate_answer.title'),
    body: tr('notifications.kind.escalate_answer.body', {
      lead: a.degraded
        ? tr('notifications.kind.escalate_answer.lead_degraded')
        : tr('notifications.kind.escalate_answer.lead'),
      filed:
        a.repoFullNames.length === 0
          ? tr('notifications.kind.escalate_answer.filed_none')
          : tr('notifications.kind.escalate_answer.filed', { repos: a.repoFullNames.join(', ') }),
    }),
  }),
);

/** The covering work's pull request, or the statement that there is none. */
function coveringPr(tr: Translator, repo: string | null, pr: number | null): string {
  if (pr === null) return tr('notifications.kind.ask_duplicate.pr_none');
  if (repo === null) return tr('notifications.kind.ask_duplicate.pr_no_repo', { pr });
  return tr('notifications.kind.ask_duplicate.pr', { repo, pr });
}

/**
 * `apps/agents-manager/src/dupe-notice.ts` `dupeAnswerNotification`. The ask is the
 * asker's own text and never rides the payload; the covering task's title is quoted.
 */
const askDuplicate = face(
  z.object({
    repoFullName: z.string().nullable(),
    prNumber: count.nullable(),
    covering_title: z.string().nullable(),
    covered: z.boolean(),
  }),
  (a, tr) => ({
    title: tr('notifications.kind.ask_duplicate.title'),
    body: tr('notifications.kind.ask_duplicate.body', {
      covering:
        a.covered && a.covering_title !== null
          ? tr('notifications.kind.ask_duplicate.covering', {
              title: a.covering_title,
              pr: coveringPr(tr, a.repoFullName, a.prNumber),
            })
          : tr('notifications.kind.ask_duplicate.covering_unread'),
    }),
  }),
);

/**
 * `apps/worker/src/notifications/onboarding-wait.ts` `planOnboardingWait`: the gaps
 * face, and the blocked face in its two voices (`cleared_by`). A blocked pass's own
 * sentence (the machine's `detail`) is not on the payload; the deep link has it.
 */
const onboardingWait = face(
  z
    .object({
      repo: text,
      verdict: z.enum(['gaps', 'blocked']),
      prerequisite: z.string().nullable(),
      cleared_by: z.enum(['human', 'box']).nullable(),
      onboarding_pull_request: count.nullable(),
    })
    .strict(),
  (a, tr) => {
    const pr = a.onboarding_pull_request;
    if (a.verdict === 'gaps') {
      return pr === null
        ? {
            title: tr('notifications.kind.onboarding_wait.title_gaps', { repo: a.repo }),
            body: tr('notifications.kind.onboarding_wait.body_gaps'),
          }
        : {
            title: tr('notifications.kind.onboarding_wait.title_gaps_pr', { repo: a.repo, pr }),
            body: tr('notifications.kind.onboarding_wait.body_gaps_pr', { repo: a.repo, pr }),
          };
    }
    const vars = {
      repo: a.repo,
      prerequisite: a.prerequisite ?? tr('notifications.kind.onboarding_wait.unnamed'),
    };
    const dirty = a.cleared_by === 'box';
    const body = dirty
      ? tr('notifications.kind.onboarding_wait.body_dirty', vars)
      : tr('notifications.kind.onboarding_wait.body_blocked', vars);
    return {
      title: dirty
        ? tr('notifications.kind.onboarding_wait.title_dirty', vars)
        : tr('notifications.kind.onboarding_wait.title_blocked', vars),
      body:
        pr === null
          ? body
          : tr('notifications.kind.onboarding_wait.with_earlier_pr', {
              body,
              earlier: tr('notifications.kind.onboarding_wait.earlier_pr', { pr }),
            }),
    };
  },
);

/**
 * `packages/domain/src/entities/key-outage-notice.ts` `keyOutageNotification`. The
 * opened body names the held counts by step, which the payload leaves out on purpose.
 */
const keyOutage = face(
  z.object({
    outageId: text,
    phase: z.enum(['opened', 'resolved']),
    closeReason: z.string().nullable().optional(),
  }),
  (a, tr, row) =>
    a.phase === 'opened'
      ? { title: tr('notifications.kind.key_outage.title_opened'), body: row.body }
      : {
          title: tr('notifications.kind.key_outage.title_resolved'),
          body:
            a.closeReason === 'picked'
              ? tr('notifications.kind.key_outage.body_resolved_picked')
              : tr('notifications.kind.key_outage.body_resolved_drained'),
        },
);

/** The escalation family, merged into `NOTIFICATION_COPY_FACES` (`notification-copy.ts`). */
export const ESCALATION_FACES = {
  escalation: { escalation },
  good_news: { escalation },
  platform_notice: { escalation },
  decision_needed: { babysit, escalation, escalate_answer: escalateAnswer },
  ask_duplicate: { default: askDuplicate },
  onboarding_wait: { default: onboardingWait },
  key_outage: { default: keyOutage },
} as const satisfies Readonly<Record<string, Readonly<Record<string, Renderer>>>>;
